import { yupResolver } from '@hookform/resolvers/yup';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FieldPath, useFieldArray, useForm } from 'react-hook-form';

import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { formatDate, isObject } from 'coreOld/utils';
import { FormType } from 'partsOld/enums';
import { useCheckActiveOverlappingUpsert, useCostingActions } from 'partsOld/hooks';
import { getActiveOverlappingCost } from 'partsOld/services/costing';
import { ICompanyPart, ICost, ICostItem, ICostForm } from 'partsOld/types';

import { costFormSchema } from './costFormSchema';
import CostFormView from './CostFormView';

const DATE_FORMAT = 'YYYY-MM-DD';

interface IProps {
  setIsFormOpen: Dispatch<SetStateAction<boolean>>;
  isFormOpen: boolean;
  part?: Partial<ICompanyPart>;
  selectedCost?: ICost;
  setSelectedCost: Dispatch<SetStateAction<ICost>>;
}

const CostForm = ({ setIsFormOpen, isFormOpen, selectedCost, setSelectedCost, part }: IProps) => {
  const isCostSelected = Boolean(selectedCost);

  const [removedItems, setRemovedItems] = useState([]);

  const { upsertCost, removeCost } = useCostingActions();
  const {
    onCancel,
    onSubmitCheck,
    onRemove,
    onCancelRemove,
    onCancelBtnClick,
    removedItem,
    isActiveItemOverlapping,
  } = useCheckActiveOverlappingUpsert<ICost>(
    FormType.COST,
    selectedCost,
    setSelectedCost,
    upsertCost,
    removeCost,
    getActiveOverlappingCost
  );

  const { showNotification } = useNotification();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
    trigger: triggerValidation,
  } = useForm<ICostForm>({
    resolver: yupResolver(costFormSchema),
    defaultValues: selectedCost,
  });

  const { fields, append, remove } = useFieldArray({
    name: 'costItems',
    keyName: 'fieldId',
    control,
  });

  useEffect(() => {
    if (fields.length < 1 && isFormOpen) {
      addCost(true);
    }
  }, [isFormOpen]);

  useEffect(() => {
    if (isObject(selectedCost)) {
      reset(selectedCost);
    }
  }, [selectedCost]);

  useEffect(() => {
    if (part) {
      reset({
        startDate: new Date(),
        uom: part?.purchaseCostUom,
        costItems: [
          {
            minQuantity: part?.orderIncrement || 1,
          },
        ],
      });
    }
  }, [isFormOpen]);

  const addCost = async (skipValidation = false) => {
    if (skipValidation) {
      append({
        maxQuantity: null,
        minQuantity: null,
        cost: null,
      });

      return;
    }

    const isValid = await triggerValidation();

    if (isValid) {
      append({
        maxQuantity: null,
        minQuantity: null,
        cost: null,
      });
    }
  };

  const removeCostItem = (index: number, item?: ICostItem) => {
    if (item.id) {
      setRemovedItems((prev) => [...prev, item.id]);
    }

    remove(index);
  };

  const onCloseModal = () => {
    reset({ uom: null });

    setRemovedItems([]);
    setIsFormOpen(false);
    setSelectedCost(null);
  };

  const onChangeDate = (date, field: FieldPath<ICostForm>) => {
    setValue(field, date);
  };

  const onSubmit =
    (skipActiveCheck = false) =>
    async (data) => {
      const dataToSend = {
        startDate: formatDate(data?.startDate, true, DATE_FORMAT),
        endDate: formatDate(data?.endDate, true, DATE_FORMAT),
        uom: data.uom,
        id: data.id,
        costItems: data.costItems,
        corporatePartId: part?.id,
        part: part?.part,
        manufacturerId: part?.manufacturerId,
        removedItems,
      };

      onSubmitCheck(dataToSend, skipActiveCheck, onCloseModal);
    };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const error = errors?.costItems?.pop()?.maxQuantity.message;

      showNotification({
        message: error,
        type: ApiStatuses.ERROR,
        position: NotificationPosition.MODAL,
      });

      return;
    }

    showNotification(null);
  }, [errors]);

  return (
    <CostFormView
      control={control}
      handleSubmit={handleSubmit}
      getValues={getValues}
      onSubmit={onSubmit}
      startDate={watch('startDate') ? new Date(watch('startDate')) : new Date()}
      endDate={watch('endDate') && new Date(watch('endDate'))}
      onChangeDate={onChangeDate as any}
      addCost={addCost}
      removeCost={removeCostItem}
      costItems={fields}
      isFormOpen={isFormOpen || Boolean(selectedCost)}
      onCloseModal={onCloseModal}
      isCostSelected={isCostSelected}
      isActiveCostOverlapping={isActiveItemOverlapping}
      onCancel={onCancel}
      onRemove={onRemove}
      onCancelRemove={onCancelRemove}
      removedItem={removedItem}
      onCancelBtnClick={onCancelBtnClick}
      setValue={setValue}
      watch={watch}
    />
  );
};

export default CostForm;
